import React, { useEffect, useState, useCallback } from "react";
import {
  AttributeListItem,
  AttributeApi,
  ATTRIBUTE_CATEGORIES,
  AttributeCategory,
} from "@/api/AttributeApi";
import IndexCard from "../../components/Card/IndexCard";
import { formatAttributeName } from "@/utils/AttributeHelper";
import LoadingPage from "../LoadingPage";

interface AttributePageProps {
  category: string;
  active?: boolean;
}

const styles = {
  pageContainer: "p-4 w-full my-auto",
  pageTitle: "text-2xl font-medium mb-4",
  gridContainer:
    "grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4",
  sectionTitle: "text-2xl font-medium mb-4",
  attributesContainer:
    "flex flex-wrap gap-4 overflow-x sm:overflow-visible w-full",
  overallAttributesContainer: "flex flex-col flex-wrap w-full",
};

const getAttributesByCategory = async (
  category: string,
  active: boolean,
): Promise<AttributeListItem[]> => {
  if (category === ATTRIBUTE_CATEGORIES.OVERALL) {
    const [environmentalAttributes, humanRightsAttributes] = await Promise.all([
      AttributeApi.getAttributeList(ATTRIBUTE_CATEGORIES.ENVIRONMENTAL, active),
      AttributeApi.getAttributeList(ATTRIBUTE_CATEGORIES.HUMANRIGHTS, active),
    ]);
    return [...environmentalAttributes, ...humanRightsAttributes];
  }
  return AttributeApi.getAttributeList(category as AttributeCategory, active);
};

const getPageTitle = (category: string) => {
  switch (category) {
    case ATTRIBUTE_CATEGORIES.ENVIRONMENTAL:
      return "Environmental Indices";
    case ATTRIBUTE_CATEGORIES.HUMANRIGHTS:
      return "Human Rights Indices";
    default:
      return "";
  }
};

const AttributePage: React.FC<AttributePageProps> = ({
  category,
  active = true,
}) => {
  const [attributes, setAttributes] = useState<AttributeListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const fetchAttributes = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const attributeList = await getAttributesByCategory(category, active);
      setAttributes(attributeList);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [category, active]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const renderAttributes = (
    filteredAttributes: AttributeListItem[],
    title: string,
  ) => (
    <div>
      {title && <h1 className={styles.sectionTitle}>{title}</h1>}
      <div className={styles.attributesContainer}>
        {filteredAttributes.map((item) => (
          <IndexCard
            key={item.attributeId}
            name={formatAttributeName(item.attributeName)}
            year={item.calendarYear}
            active={item.isActive}
            vendor={`Verisk Maplecroft`}
          />
        ))}
      </div>
    </div>
  );

  const renderOverallAttributes = () => (
    <div className={styles.overallAttributesContainer}>
      {renderAttributes(
        attributes.filter((attr) => attr.categoryName === "Environmental"),
        "Environmental Indices",
      )}
      {renderAttributes(
        attributes.filter((attr) => attr.categoryName === "Human Rights"),
        "Human Rights Indices",
      )}
    </div>
  );

  const showError = () => (
    <div className="flex flex-col items-center justify-center pt-[100px]">
      <p className="text-red-500 text-lg font-medium mb-2">
        Unable to load attribute data
      </p>
      <p className="text-gray-600">
        There was a problem retrieving the attribute information. Please try
        again later.
      </p>
      <button
        type="button"
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={() => {
          void fetchAttributes();
        }}
      >
        Retry
      </button>
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      {loading ? (
        <LoadingPage title="Loading attributes..." />
      ) : error ? (
        showError()
      ) : (
        <div>
          <h1 className={styles.pageTitle}>{getPageTitle(category)}</h1>
          <div className="flex w-full justify-center">
            {category === ATTRIBUTE_CATEGORIES.OVERALL ? (
              renderOverallAttributes()
            ) : attributes.length > 0 ? (
              renderAttributes(attributes, "")
            ) : (
              <p>No indices available for this category.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributePage;
